import { ParamValue } from '../../../../../../components/Crud/useFilter.types';
import {
  ListSortViewEnum,
  OrderHeadersGroupByEnum,
} from '../../../../products/orderProducts.types';
import { getGroupByInfo } from '../../../../products/orderProducts.utils';
import { getDefaultOptions } from '../../../../products/review/orderProductsReview.types';
import {
  BuildViewOptionsParams,
  GetAppliedViewSelectionParams,
  OrderProductsViewOption,
  OrderProductsViewSelection,
  OrderProductsViewUrlSelection,
} from './orderProductsViewButton.types';

const OPTION_KEY_SEPARATOR = ':';

const NON_SUBGROUPABLE_VALUES: string[] = [
  OrderHeadersGroupByEnum.DEFAULT,
  OrderHeadersGroupByEnum.PENDENCY,
];

const NON_SUBGROUP_OPTION_VALUES: string[] = [OrderHeadersGroupByEnum.PENDENCY];

export const DEFAULT_VIEW_SELECTION: OrderProductsViewSelection = {
  groupByKey: OrderHeadersGroupByEnum.DEFAULT,
  subGroupByKey: OrderHeadersGroupByEnum.DEFAULT,
  sortByKey: ListSortViewEnum.LAST_ADDED,
  shouldShowOrderTotals: true,
};

export const getOptionKey = (value: string, classificationTypeId?: string) =>
  classificationTypeId ? `${value}${OPTION_KEY_SEPARATOR}${classificationTypeId}` : value;

export const parseOptionKey = (optionKey: string) => {
  const [value, classificationTypeId] = optionKey.split(OPTION_KEY_SEPARATOR);

  return { value, classificationTypeId };
};

export const hasGrouping = (groupByKey: string) =>
  parseOptionKey(groupByKey).value !== OrderHeadersGroupByEnum.DEFAULT;

export const canSubGroupBy = (groupByKey: string) =>
  !NON_SUBGROUPABLE_VALUES.includes(parseOptionKey(groupByKey).value);

export const isSameViewSelection = (
  selection: OrderProductsViewSelection,
  otherSelection: OrderProductsViewSelection,
) =>
  selection.groupByKey === otherSelection.groupByKey &&
  selection.subGroupByKey === otherSelection.subGroupByKey &&
  selection.sortByKey === otherSelection.sortByKey &&
  selection.shouldShowOrderTotals === otherSelection.shouldShowOrderTotals;

export const buildViewOptions = ({
  isOrderEditPage,
  classificationTypes,
}: BuildViewOptionsParams) => {
  const defaultOptions = getDefaultOptions({ isOrderEditPage });

  const groupBy: OrderProductsViewOption[] = [
    ...defaultOptions.groupBy.map((option) => ({
      key: option.value as string,
      label: option.label,
    })),
    ...classificationTypes.map((classificationType) => ({
      key: getOptionKey(OrderHeadersGroupByEnum.COLOR_CLASSIFICATION_TYPE, classificationType.id),
      label: classificationType.name,
    })),
  ];

  const sortBy: OrderProductsViewOption[] = defaultOptions.sortBy.map((option) => ({
    key: option.value as string,
    label: option.label,
  }));

  return { groupBy, sortBy };
};

export const getSubGroupByOptions = (
  groupByOptions: OrderProductsViewOption[],
  groupByKey: string,
) =>
  groupByOptions.filter((option) => {
    const { value } = parseOptionKey(option.key);

    if (value === OrderHeadersGroupByEnum.DEFAULT) {
      return true;
    }

    return option.key !== groupByKey && !NON_SUBGROUP_OPTION_VALUES.includes(value);
  });

const toStringList = (value?: ParamValue): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  return typeof value === 'string' ? [value] : [];
};

const getClassificationIds = (groupByInfo?: ParamValue) => {
  const info = getGroupByInfo(typeof groupByInfo === 'string' ? groupByInfo : null);

  return toStringList(info?.classificationTypeId);
};

export const getAppliedViewSelection = ({
  groupBy,
  groupByInfo,
  sortBy,
}: GetAppliedViewSelectionParams): OrderProductsViewUrlSelection => {
  const groupByValues = toStringList(groupBy);
  const classificationIds = getClassificationIds(groupByInfo);

  const groupByValue = groupByValues[0] ?? DEFAULT_VIEW_SELECTION.groupByKey;
  const subGroupByValue = groupByValues[1] ?? DEFAULT_VIEW_SELECTION.subGroupByKey;
  const isGroupByClassification =
    groupByValue === OrderHeadersGroupByEnum.COLOR_CLASSIFICATION_TYPE;

  return {
    groupByKey: isGroupByClassification
      ? getOptionKey(groupByValue, classificationIds[0])
      : groupByValue,
    subGroupByKey:
      subGroupByValue === OrderHeadersGroupByEnum.COLOR_CLASSIFICATION_TYPE
        ? getOptionKey(subGroupByValue, classificationIds[isGroupByClassification ? 1 : 0])
        : subGroupByValue,
    sortByKey: toStringList(sortBy)[0] ?? DEFAULT_VIEW_SELECTION.sortByKey,
  };
};
